<script setup lang="ts">
import type { TFileImageSource } from '@/composables/files/useFileImage';
import { useFileImage } from '@/composables/files/useFileImage';
import { useLoadWhenVisible } from '@/composables/useLoadWhenVisible';
import ImageIcon from '@/assets/icons/ImageIcon.vue';
import { computed, ref, useAttrs, useTemplateRef, watch } from 'vue';

defineOptions({ inheritAttrs: false });
const props = withDefaults(
  defineProps<{ source: TFileImageSource; placeholderSize?: 'sm' | 'md'; loading?: 'eager' | 'lazy' }>(),
  { placeholderSize: 'md', loading: 'eager' },
);
const emit = defineEmits<{ error: [cause: unknown]; load: [event: Event] }>();
const imageElement = useTemplateRef<HTMLElement>('imageElement');
const enabled = useLoadWhenVisible(imageElement, { eager: () => props.loading === 'eager' });
const { url, error, isFetching } = useFileImage(() => props.source, enabled);
const attrs = useAttrs();
const hasLoaded = ref(false);
const displayUrl = computed(() => (url.value && (!isFetching.value || hasLoaded.value) ? url.value : undefined));
const placeholderAttrs = computed(() =>
  Object.fromEntries(Object.entries(attrs).filter(([key]) => key !== 'alt' && key !== 'decoding')),
);
const placeholderIconClass = computed(() => (props.placeholderSize === 'sm' ? 'size-[24px]' : 'size-[48px]'));
watch(
  () => props.source,
  () => {
    hasLoaded.value = false;
  },
);
watch([url, isFetching], ([nextUrl, fetching]) => {
  if (nextUrl && !fetching) hasLoaded.value = true;
});
watch(error, (cause) => {
  if (cause) emit('error', cause);
});
</script>

<template>
  <img
    v-if="displayUrl"
    ref="imageElement"
    v-bind="attrs"
    :src="displayUrl"
    @error="emit('error', $event)"
    @load="emit('load', $event)"
  />
  <div
    v-else
    ref="imageElement"
    v-bind="placeholderAttrs"
    class="flex items-center justify-center bg-surface-tertiary"
    aria-hidden="true"
  >
    <ImageIcon :class="[placeholderIconClass, 'text-tertiary motion-safe:animate-file-image-placeholder']" />
  </div>
</template>
